import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, inject, signal, computed, effect } from '@angular/core';
import { RouterLink } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { FileUploadModule } from 'primeng/fileupload';
import { TagModule } from 'primeng/tag';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ProgressBarModule } from 'primeng/progressbar';
import { TooltipModule } from 'primeng/tooltip';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { MessageModule } from 'primeng/message';
import { AutoCompleteModule, AutoCompleteCompleteEvent } from 'primeng/autocomplete';
import { SelectModule } from 'primeng/select';
import { CloudApiService, FirmwareEntry } from '@/cloud/services/cloud-api.service';
import { MessageService, ConfirmationService } from 'primeng/api';
import { BreadcrumbsComponent, BreadcrumbItem } from '@/shared/breadcrumbs/breadcrumbs.component';
import { EmptyStateComponent } from '@/shared/ui/empty-state/empty-state.component';
import { LoadingSkeletonComponent } from '@/shared/ui/loading-skeleton/loading-skeleton.component';
import { TranslationService } from '@/layout/service/translation.service';

@Component({
    selector: 'app-firmware',
    standalone: true,
    imports: [
        CommonModule,
        RouterLink,
        BreadcrumbsComponent,
        EmptyStateComponent,
        LoadingSkeletonComponent,
        FormsModule,
        ButtonModule,
        TableModule,
        DialogModule,
        InputTextModule,
        FileUploadModule,
        TagModule,
        ProgressSpinnerModule,
        ProgressBarModule,
        TooltipModule,
        ConfirmDialogModule,
        MessageModule,
        AutoCompleteModule,
        SelectModule
    ],
    providers: [ConfirmationService],
    templateUrl: './firmware.html',
    styleUrls: ['./firmware.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class Firmware implements OnInit {
    private api = inject(CloudApiService);
    private messageService = inject(MessageService);
    private confirmationService = inject(ConfirmationService);
    readonly t = inject(TranslationService).t;
    private request = 0;
    readonly loadError = signal(false);
    readonly search = signal('');
    readonly typeFilter = signal<'all' | 'puf' | 'zip'>('all');
    readonly tableFirst = signal(0);
    readonly totalSize = computed(() => this.firmware().reduce((total, fw) => total + fw.fileSizeBytes, 0));
    readonly hasFilters = computed(() => !!this.search().trim() || !!this.filterModel().trim() || this.typeFilter() !== 'all');
    readonly typeOptions = computed(() => [
        { label: this.t()('management.allTypes'), value: 'all' },
        { label: 'PUF', value: 'puf' },
        { label: 'ZIP', value: 'zip' }
    ]);
    readonly libraryBreadcrumbs = computed<BreadcrumbItem[]>(() => [
        { label: this.t()('management.dashboard'), routerLink: '/dashboard' },
        { label: this.t()('management.firmware'), routerLink: '/firmware' }
    ]);

    constructor() {
        inject(DestroyRef).onDestroy(() => ++this.request);
        effect(() => {
            this.search();
            this.filterModel();
            this.typeFilter();
            this.tableFirst.set(0);
        });
    }

    // State
    firmware = signal<FirmwareEntry[]>([]);
    loading = signal(false);
    uploading = signal(false);
    uploadProgress = signal(0);
    uploadStatus = signal('');

    // Filters
    filterModel = signal('');
    modelSuggestions = signal<string[]>([]);

    // Detail dialog
    showDetailDialog = signal(false);
    selectedFirmware = signal<FirmwareEntry | null>(null);
    selectedAutoUpdateModel = signal<string | null>(null);
    loadingDetail = signal(false);

    // Rename
    showRenameDialog = signal(false);
    renameValue = signal('');
    renaming = signal(false);

    // Upload dialog
    showUploadDialog = signal(false);

    // Computed: unique models from all firmware
    allModels = computed(() => {
        const models = new Set<string>();
        for (const fw of this.firmware()) {
            for (const m of fw.compatibleModels || []) {
                models.add(m);
            }
        }
        return Array.from(models).sort();
    });

    // Computed: filtered firmware list
    filteredFirmware = computed(() => {
        let list = this.firmware();
        const model = this.filterModel().trim().toLowerCase();

        if (model) {
            list = list.filter(fw =>
                fw.compatibleModels?.some(m => m.toLowerCase().includes(model))
            );
        }
        // if filter is empty, show all firmware
        else {
            list = this.firmware();
        }
        const term = this.search().trim().toLowerCase();
        const type = this.typeFilter();
        return list.filter(fw => (type === 'all' || (fw.firmwareType || 'puf').toLowerCase() === type) &&
            (!term || [fw.name, fw.description, fw.version, fw.originalFileName, ...(fw.compatibleModels || [])].some(value => value?.toLowerCase().includes(term))));
    });

    ngOnInit() {
        this.loadFirmware();
    }

    async loadFirmware() {
        const request = ++this.request;
        this.loading.set(true);
        this.loadError.set(false);
        try {
            const result = await this.api.getFirmwareList();
            if (request !== this.request) return;
            this.firmware.set(result);
            this.tableFirst.set(0);
        } catch {
            if (request !== this.request) return;
            this.loadError.set(true);
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load firmware list' });
        } finally {
            if (request === this.request) this.loading.set(false);
        }
    }

    async viewFirmware(fw: FirmwareEntry) {
        this.selectedFirmware.set(fw);
        this.selectedAutoUpdateModel.set(fw.compatibleModels?.[0] || null);
        this.showDetailDialog.set(true);
        this.loadingDetail.set(true);
        try {
            const details = await this.api.getFirmwareDetails(fw.id);
            this.selectedFirmware.set(details);
            if (!this.selectedAutoUpdateModel() && details.compatibleModels?.length) {
                this.selectedAutoUpdateModel.set(details.compatibleModels[0]);
            }
        } catch (err: any) {
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load firmware details' });
        } finally {
            this.loadingDetail.set(false);
        }
    }

    async downloadFirmware(fw: FirmwareEntry) {
        try {
            const result = await this.api.getFirmwareDownloadUrl(fw.id);
            window.open(result.downloadUrl, '_blank');
        } catch (err: any) {
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to get download URL' });
        }
    }

    async onFileSelect(event: any) {
        const file = event.files[0];
        if (!file) return;

        if (!file.name.endsWith('.puf') && !file.name.endsWith('.zip')) {
            this.messageService.add({ severity: 'error', summary: 'Invalid File', detail: 'Only PUF and ZIP firmware files are allowed' });
            return;
        }

        this.uploading.set(true);
        this.uploadProgress.set(0);
        this.uploadStatus.set('Uploading firmware...');
        try {
            const result = await this.api.uploadFirmware(
                file,
                (percent) => this.uploadProgress.set(percent),
                (status) => this.uploadStatus.set(status)
            );
            this.uploadStatus.set('Complete!');
            this.messageService.add({
                severity: 'success',
                summary: 'Upload Complete',
                detail: `${result.name} v${result.version} uploaded successfully (${result.compatibleModels.length} compatible models)`
            });
            this.showUploadDialog.set(false);
            await this.loadFirmware();
        } catch (err: any) {
            const detail = err?.error?.error || err?.message || 'Upload failed';
            this.messageService.add({ severity: 'error', summary: 'Upload Failed', detail });
        } finally {
            this.uploading.set(false);
            this.uploadProgress.set(0);
            this.uploadStatus.set('');
        }
    }

    confirmDelete(fw: FirmwareEntry) {
        this.confirmationService.confirm({
            message: `Are you sure you want to delete "${fw.name}" v${fw.version}?`,
            header: 'Confirm Delete',
            icon: 'pi pi-exclamation-triangle',
            accept: () => this.deleteFirmware(fw)
        });
    }

    async deleteFirmware(fw: FirmwareEntry) {
        try {
            await this.api.deleteFirmware(fw.id);
            this.messageService.add({ severity: 'success', summary: 'Deleted', detail: `${fw.name} v${fw.version} deleted` });
            this.showDetailDialog.set(false);
            await this.loadFirmware();
        } catch (err: any) {
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to delete firmware' });
        }
    }

    openRename(fw: FirmwareEntry) {
        this.renameValue.set(fw.name);
        this.showRenameDialog.set(true);
    }

    async submitRename() {
        const fw = this.selectedFirmware();
        const newName = this.renameValue().trim();
        if (!fw || !newName) return;

        this.renaming.set(true);
        try {
            await this.api.renameFirmware(fw.id, newName);
            this.messageService.add({ severity: 'success', summary: 'Renamed', detail: `Firmware renamed to "${newName}"` });
            this.showRenameDialog.set(false);
            this.selectedFirmware.set({ ...fw, name: newName });
            await this.loadFirmware();
        } catch (err: any) {
            const detail = err?.error?.error || err?.error || err?.message || 'Rename failed';
            this.messageService.add({ severity: 'error', summary: 'Rename Failed', detail });
        } finally {
            this.renaming.set(false);
        }
    }

    // Check if current user can manage firmware (superadmin or Admin in any namespace)
    get canManageFirmware(): boolean {
        const user = this.api.currentUser();
        if (!user) return false;
        if (user.superAdmin) return true;
        // Admin role in any namespace (role === 2 is Admin)
        return Array.isArray(user.permissions) && user.permissions.some((p: any) => (p?.role === 2 || p?.Role === 2));
    }

    formatFileSize(bytes: number): string {
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
        return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    }

    formatDate(dateStr: string): string {
        if (!dateStr) return '';
        return new Date(dateStr).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    }

    formatDateOnly(dateStr: string | undefined): string {
        if (!dateStr) return '';
        // CreationDate from PUF metadata may be in various formats
        const d = new Date(dateStr);
        if (isNaN(d.getTime())) return dateStr;
        return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    }

    searchModels(event: AutoCompleteCompleteEvent) {
        const query = (event.query || '').toLowerCase();
        const all = this.allModels();
        if (!query) {
            this.modelSuggestions.set(all);
        } else {
            this.modelSuggestions.set(all.filter(m => m.toLowerCase().includes(query)));
        }
    }

    getPublicDownloadUrl(model: string, firmwareType: string = 'puf'): string {
        const base = window.location.origin;
        const ext = firmwareType === 'zip' ? 'zip' : 'puf';
        return `${base}/api/firmware/public/${encodeURIComponent(model)}/latest.${ext}`;
    }

    getPublicHashUrl(model: string, firmwareType: string = 'puf'): string {
        const base = window.location.origin;
        const ext = firmwareType === 'zip' ? 'zip' : 'puf';
        return `${base}/api/firmware/public/${encodeURIComponent(model)}/latest.${ext}.hash`;
    }

    getVersionedDownloadUrl(model: string, originalFileName: string): string {
        const base = window.location.origin;
        return `${base}/api/firmware/public/${encodeURIComponent(model)}/${encodeURIComponent(originalFileName)}`;
    }

    getVersionedHashUrl(model: string, originalFileName: string): string {
        const base = window.location.origin;
        return `${base}/api/firmware/public/${encodeURIComponent(model)}/${encodeURIComponent(originalFileName)}.hash`;
    }

    copyToClipboard(text: string) {
        navigator.clipboard.writeText(text).then(() => {
            this.messageService.add({ severity: 'success', summary: 'Copied', detail: 'URL copied to clipboard', life: 2000 });
        }).catch(() => {
            this.messageService.add({ severity: 'error', summary: 'Copy Failed', detail: 'Clipboard access denied', life: 3000 });
        });
    }

    resetFilter() {
        this.filterModel.set('');
        this.modelSuggestions.set(this.allModels());
    }

    clearFilters() {
        this.search.set('');
        this.typeFilter.set('all');
        this.resetFilter();
    }
}
